import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { getIndex, getObjectTypeLabel, nextDefaultName, normalizeEntityName, isNameTaken, toPropertiesType } from "utils/functions.utils";
import { EditorState, PropertiesTypes, TargetImageData, TargetImageObject, TransformType } from "utils/interface.utils";

const initialState: EditorState = {
    targetImageDataList: [],
    currentSelectedPropertyType: null,
    selectedTargetIndex: null,
    selectedTargetImageID:null,
    selectedObjectID:null,
    zoomPercentage:100,
    selectedTransformControl:null,  
    keepToolForObjectID: null,
    lastDeletedTargetImageData: null,
    isTargetImageListItemClicked: false,
    isObjectListItemClicked: false,
    isUpdatingFromEditorWorkspace: false,
    shouldUpdateObjectState: false,
    history: [],
    future: [],
    isGestureInProgress: false,
    hasUnsavedChanges: false,
}

const MAX_HISTORY_ENTRIES = 50;

const pushHistorySnapshot = (state: EditorState, snapshot: Array<TargetImageData> = state.targetImageDataList) => {
    state.history.push(JSON.parse(JSON.stringify(snapshot)));
    if (state.history.length > MAX_HISTORY_ENTRIES) {
        state.history.shift();
    }
    state.future = [];
    state.hasUnsavedChanges = true;
}

// Undo/redo can remove the target or object the selection points at. Left there, the Properties
// panel reads a missing object as a new one and re-creates it, so fall back to what still exists.
const dropStaleSelection = (state: EditorState) => {
    const list = state.targetImageDataList;
    if (state.selectedTargetImageID && !list.some((target) => target.id === state.selectedTargetImageID)) {
        const last = list[list.length - 1];
        state.selectedTargetIndex = last ? list.length - 1 : null;
        state.selectedTargetImageID = last?.id ?? null;
        state.selectedObjectID = null;
        state.currentSelectedPropertyType = last ? PropertiesTypes.TARGET : null;
        state.selectedTransformControl = null;
        return;
    }
    if (!state.selectedObjectID) return;
    const objects = state.targetImageDataList[state.selectedTargetIndex ?? 0]?.objects ?? [];
    if (objects.some((object) => object.id === state.selectedObjectID)) return;
    state.selectedObjectID = null;
    state.currentSelectedPropertyType = PropertiesTypes.TARGET;
    state.selectedTransformControl = null;
}

// Undo/redo that bring back a target (an undone upload) or an object (an undone add or delete) focus
// it, as the original action did, instead of leaving the author with nothing selected.
const focusRestored = (state: EditorState, previousList: Array<TargetImageData>): boolean => {
    const previousTargetIDs = new Set(previousList.map((target) => target.id));
    const previousObjectIDs = new Set(previousList.flatMap((target) => target.objects.map((object) => object.id)));
    const list = state.targetImageDataList;

    const restoredTargetIndex = list.findIndex((target) => !previousTargetIDs.has(target.id));
    if (restoredTargetIndex !== -1) {
        state.selectedTargetIndex = restoredTargetIndex;
        state.selectedTargetImageID = list[restoredTargetIndex].id;
        state.selectedObjectID = null;
        state.currentSelectedPropertyType = PropertiesTypes.TARGET;
        state.selectedTransformControl = null;
        return true;
    }

    for (let targetIndex = 0; targetIndex < list.length; targetIndex += 1) {
        const restoredObject = list[targetIndex].objects.find((object) => !previousObjectIDs.has(object.id));
        if (restoredObject) {
            state.selectedTargetIndex = targetIndex;
            state.selectedTargetImageID = list[targetIndex].id;
            state.selectedObjectID = restoredObject.id;
            state.currentSelectedPropertyType = toPropertiesType(restoredObject.type) ?? PropertiesTypes.TARGET;
            state.selectedTransformControl = null;
            return true;
        }
    }
    return false;
}

// History is one stack per project, so a step can change a target other than the one on screen.
// Switch to that target (and to the object whose properties changed, if any) so the change is visible.
const focusChangedTarget = (state: EditorState, previousList: Array<TargetImageData>) => {
    const list = state.targetImageDataList;
    const changedIndex = list.findIndex((target) => {
        const before = previousList.find((previous) => previous.id === target.id);
        return !!before && JSON.stringify(before) !== JSON.stringify(target);
    });
    if (changedIndex === -1 || list[changedIndex].id === state.selectedTargetImageID) return;

    const before = previousList.find((previous) => previous.id === list[changedIndex].id);
    const editedObject = list[changedIndex].objects.find((object) => {
        const previousObject = before?.objects.find((candidate) => candidate.id === object.id);
        return !!previousObject && JSON.stringify(previousObject) !== JSON.stringify(object);
    });
    state.selectedTargetIndex = changedIndex;
    state.selectedTargetImageID = list[changedIndex].id;
    state.selectedObjectID = editedObject?.id ?? null;
    state.currentSelectedPropertyType = (editedObject && toPropertiesType(editedObject.type)) ?? PropertiesTypes.TARGET;
    state.selectedTransformControl = null;
}

// Runs after every undo/redo. When the step moved the selection to a different target, the
// workspace is told to redraw it the same way a click in the target list does.
const focusAfterHistoryStep = (state: EditorState, previousList: Array<TargetImageData>) => {
    const previousTargetID = state.selectedTargetImageID;
    dropStaleSelection(state);
    if (!focusRestored(state, previousList)) focusChangedTarget(state, previousList);
    if (state.selectedTargetImageID && state.selectedTargetImageID !== previousTargetID) {
        state.isTargetImageListItemClicked = true;
    }
}

const editorSlice = createSlice({
    name: "editor",
    initialState,
    reducers: {
        setTargetImageDataList: (state, action: PayloadAction<Array<TargetImageData>>) => {
            state.targetImageDataList = action.payload;
        },

        // A project was opened: its targets replace the list, and undo history starts fresh so
        // undo can never bring back another project's targets.
        loadProjectData: (state, action: PayloadAction<Array<TargetImageData>>) => {
            state.targetImageDataList = action.payload;
            state.history = [];
            state.future = [];
            state.isGestureInProgress = false;
        },

        setCurrentSelectedPropertyType: (state, action: PayloadAction<PropertiesTypes | null>) => {
            state.currentSelectedPropertyType = action.payload;
        },

        addTargetImage: (state, action: PayloadAction<TargetImageData>) => {
            // Adding an empty target slot is not undoable; uploading its image is (updateTargetImage).
            const name = action.payload.name ?? nextDefaultName('Target', state.targetImageDataList.map((target) => target.name ?? ''), state.targetImageDataList.length);
            state.targetImageDataList.push({ ...action.payload, name });
            state.hasUnsavedChanges = true;
        },

        updateTargetImage: (state, action: PayloadAction<TargetImageData>) => {
            const index = getIndex(action.payload.id,state.targetImageDataList);
            if (index !== -1) {
                // Uploading or replacing the image is an author action. Other updates (contained
                // dimensions from the workspace, the compiled targetId, reordering) are not.
                const previous = state.targetImageDataList[index];
                if (previous.targetImage !== action.payload.targetImage) {
                    // The first upload into an empty slot is what creates the target for the
                    // author, so undoing it removes the slot rather than leaving an empty one.
                    pushHistorySnapshot(state, previous.targetImage
                        ? state.targetImageDataList
                        : state.targetImageDataList.filter((target) => target.id !== previous.id));
                }
                // Callers rebuild the target without its name, so a missing one keeps what was there.
                state.targetImageDataList[index] = {...action.payload, name: action.payload.name ?? previous.name};
                state.hasUnsavedChanges = true;
            }
        },

        deleteTargetImage: (state, action: PayloadAction<string>) => {
            const index = getIndex(action.payload,state.targetImageDataList);
            if (index !== -1){
                state.targetImageDataList.splice(index,1)
                state.hasUnsavedChanges = true;

                if (state.selectedTargetImageID === action.payload) {
                    if (state.targetImageDataList.length > 0) {
                        const newFirstTargetImage = state.targetImageDataList[state.targetImageDataList.length - 1];
                        state.selectedTargetIndex = state.targetImageDataList.length - 1
                        state.selectedTargetImageID = newFirstTargetImage?.id
                        state.selectedObjectID = newFirstTargetImage?.objects[0]?.id
                        state.currentSelectedPropertyType = newFirstTargetImage?.objects.length > 0 ? newFirstTargetImage?.objects[0]?.type as PropertiesTypes : PropertiesTypes.TARGET
                    } else {
                        state.targetImageDataList = []
                        state.selectedTargetIndex = null
                        state.selectedTargetImageID = null
                        state.selectedObjectID = null
                        state.currentSelectedPropertyType = null
                    }
                }
            }
        },

        setSelectedTargetIndex: (state, action: PayloadAction<number | null>) => {
            state.selectedTargetIndex = action.payload;
            
            // Update selected target image ID when changing target index
            // if (action.payload !== null && state.targetImageDataList[action.payload]) {
            //     state.selectedTargetImageID = state.targetImageDataList[action.payload].id;
                
            //     // Set selectedObjectID to the first object of the new target image or null
            //     const objects = state.targetImageDataList[action.payload].objects;
            //     state.selectedObjectID = objects.length > 0 ? objects[0].id : null;
            //     state.currentSelectedPropertyType = objects.length > 0 ? objects[0].type : PropertiesTypes.TARGET;
            // } else {
            //     state.selectedTargetImageID = null;
            //     state.selectedObjectID = null;
            //     state.currentSelectedPropertyType = PropertiesTypes.TARGET;
            // }
        },
        
        setSelectedTargetImageID: (state,action: PayloadAction<string | null>) => {
            state.selectedTargetImageID = action.payload
        },

        addObjectsToTargetImage: (state,action: PayloadAction<{targetImageID:string,object:TargetImageObject}>) => {
            const index = getIndex(action.payload.targetImageID,state.targetImageDataList)
            if(index !== -1){
                pushHistorySnapshot(state);
                const objects = state.targetImageDataList[index].objects;
                const name = action.payload.object.name ?? nextDefaultName(getObjectTypeLabel(action.payload.object.type), objects.map((object) => object.name ?? ''), objects.filter((object) => object.type === action.payload.object.type).length);
                objects.unshift({ ...action.payload.object, name })
                // A freshly added object opens with the Position tool, so its box is there to drag.
                // Selecting an object clears the tool, so this has to happen here, when it lands.
                if (action.payload.object.id === state.selectedObjectID) {
                    state.selectedTransformControl = TransformType.POSITION;
                }
            }
        },

        updateObjectInTargetImage: (state,action: PayloadAction<{targetImageID:string,object:TargetImageObject,commitHistory?:boolean,skipHistory?:boolean}>) => {
            const index = getIndex(action.payload.targetImageID,state.targetImageDataList)
            if(index !== -1){
                const objectIndex = state.targetImageDataList[index].objects.findIndex(object => object.id === action.payload.object.id);
                if(objectIndex !== -1){
                    if (action.payload.skipHistory) {
                        // Programmatic/derived correction (e.g. recomputed text/button mesh
                        // dimensions), not a user edit — must not create or fight an undo step.
                    } else if (action.payload.commitHistory === false) {
                        state.hasUnsavedChanges = true;
                        // Mid-gesture frame (e.g. a drag). Snapshot only once, at the start of
                        // the gesture, so the whole gesture collapses into a single undo step
                        // that restores the pre-gesture state rather than the last frame before commit.
                        if (!state.isGestureInProgress) {
                            pushHistorySnapshot(state);
                            state.isGestureInProgress = true;
                        }
                    } else if (state.isGestureInProgress) {
                        // Final frame of a gesture that already snapshotted at its start.
                        state.isGestureInProgress = false;
                        state.hasUnsavedChanges = true;
                    } else {
                        pushHistorySnapshot(state);
                    }
                    state.targetImageDataList[index].objects[objectIndex].properties = action.payload.object.properties
                }
            }
        },

        // Rename is an author action: undoable, and ignored when the name is unusable or already taken in its scope.
        renameTargetImage: (state, action: PayloadAction<{ targetImageID: string, name: string }>) => {
            const index = getIndex(action.payload.targetImageID, state.targetImageDataList);
            const name = normalizeEntityName(action.payload.name);
            if (index === -1 || !name) return;
            if (isNameTaken(state.targetImageDataList.map((target) => target.name ?? ''), name, index)) return;
            pushHistorySnapshot(state);
            state.targetImageDataList[index].name = name;
            state.hasUnsavedChanges = true;
        },

        renameObjectInTargetImage: (state, action: PayloadAction<{ targetImageID: string, objectID: string, name: string }>) => {
            const index = getIndex(action.payload.targetImageID, state.targetImageDataList);
            const name = normalizeEntityName(action.payload.name);
            if (index === -1 || !name) return;
            const objects = state.targetImageDataList[index].objects;
            const objectIndex = objects.findIndex((object) => object.id === action.payload.objectID);
            if (objectIndex === -1 || isNameTaken(objects.map((object) => object.name ?? ''), name, objectIndex)) return;
            pushHistorySnapshot(state);
            objects[objectIndex].name = name;
            state.hasUnsavedChanges = true;
        },

        deleteObjectOfTargetImage: (state,action: PayloadAction<string>) => {
            const objects = state.targetImageDataList[state.selectedTargetIndex ?? 0]?.objects
            const shouldDeleteObjectIndex = objects.findIndex((object) => object.id === action.payload);
            if(shouldDeleteObjectIndex !== -1) {
                pushHistorySnapshot(state);
                objects.splice(shouldDeleteObjectIndex,1)

                if (objects.length > 0) {
                    const lastObject = objects[objects.length - 1];
                    state.selectedObjectID = lastObject.id;
                    state.currentSelectedPropertyType = lastObject.type as PropertiesTypes;
                } else {
                    state.selectedObjectID = null;
                    state.currentSelectedPropertyType = PropertiesTypes.TARGET;
                }
            }
        },

        setSelectedObjectID: (state,action: PayloadAction<string | null>) => {
            state.selectedObjectID = action.payload
            state.keepToolForObjectID = null
        },

        setTargetImageSelection: (state, action) => { 
            const {
                selectedObjectID, 
                currentSelectedPropertyType, 
                selectedTargetIndex, 
                selectedTargetImageID, 
                isTargetImageListItemClicked, 
                shouldUpdateObjectState
            } = action.payload;

            state.selectedObjectID = selectedObjectID;
            state.currentSelectedPropertyType = currentSelectedPropertyType;
            state.selectedTargetIndex = selectedTargetIndex;
            state.selectedTargetImageID = selectedTargetImageID;
            state.isTargetImageListItemClicked = isTargetImageListItemClicked;
            state.shouldUpdateObjectState = shouldUpdateObjectState;
            // The tool belongs to the previously selected object. The workspace only resets it when
            // an object becomes selected, which a target-only selection never does.
            state.selectedTransformControl = null;
        },

        setZoomPercentage: (state, action: PayloadAction<number>) => {
            state.zoomPercentage = action.payload;
        },

        resetZoomPercentage: (state) => {
            state.zoomPercentage = 100
        },

        // Clicking an object (on the canvas or in the Objects list) selects it and keeps the tool in
        // use (Position when none) so the object's box shows.
        selectObjectKeepingTool: (state, action: PayloadAction<{ objectID: string, objectType: string }>) => {
            state.selectedObjectID = action.payload.objectID;
            state.keepToolForObjectID = action.payload.objectID;
            state.currentSelectedPropertyType = toPropertiesType(action.payload.objectType) ?? state.currentSelectedPropertyType;
            state.selectedTransformControl = state.selectedTransformControl ?? TransformType.POSITION;
            state.shouldUpdateObjectState = true;
        },

        setSelectedTransformControl: (state,action: PayloadAction<TransformType | null>) => {
            state.selectedTransformControl = action.payload
        },

        setIsTargetImageListItemClicked: (state, action: PayloadAction<boolean>) => {
            state.isTargetImageListItemClicked = action.payload
        },

        setIsObjectListItemClicked: (state, action: PayloadAction<boolean>) => {
            state.isObjectListItemClicked = action.payload
        },

        setLastDeletedTargetImageData: (state, action: PayloadAction<TargetImageData | null>) => {
            state.lastDeletedTargetImageData = action.payload
        },

        setIsUpdatingFromEditorWorkspace: (state, action: PayloadAction<boolean>) => {
            state.isUpdatingFromEditorWorkspace = action.payload
        },  

        setShouldUpdateObjectState: (state, action: PayloadAction<boolean>) => {
            state.shouldUpdateObjectState = action.payload
        },

        // Undo/redo swap targetImageDataList wholesale behind the Properties panel's back. The
        // panel hydrates its local form state from the store only when shouldUpdateObjectState is
        // raised (see its effect in properties.component.tsx, keyed on that flag), so without
        // raising it here the scene rolls back while the panel keeps showing the pre-undo values —
        // and the panel's next edit writes that stale property set back, reverting the undo.
        // Only raise it when something actually moved, so a no-op undo doesn't discard input.
        undo: (state) => {
            const previous = state.history.pop();
            if (previous) {
                state.future.push(state.targetImageDataList);
                state.targetImageDataList = previous;
                state.hasUnsavedChanges = true;
                state.shouldUpdateObjectState = true;
                focusAfterHistoryStep(state, state.future[state.future.length - 1]);
            }
        },

        // The scene now matches what is on the server (project loaded, or a save succeeded).
        markSaved: (state) => {
            state.hasUnsavedChanges = false;
        },

        // Leaving without saving: this project's undo/redo stacks must not leak into the next one.
        clearHistory: (state) => {
            state.history = [];
            state.future = [];
            state.isGestureInProgress = false;
        },

        redo: (state) => {
            const next = state.future.pop();
            if (next) {
                state.history.push(state.targetImageDataList);
                state.targetImageDataList = next;
                state.hasUnsavedChanges = true;
                state.shouldUpdateObjectState = true;
                focusAfterHistoryStep(state, state.history[state.history.length - 1]);
            }
        },
    }
})

export const {
    setTargetImageDataList,
    setCurrentSelectedPropertyType, 
    setSelectedTargetImageID,
    addTargetImage, 
    updateTargetImage, 
    deleteTargetImage, 
    setSelectedTargetIndex, 
    addObjectsToTargetImage, 
    updateObjectInTargetImage,
    renameTargetImage,
    renameObjectInTargetImage,
    deleteObjectOfTargetImage, 
    setSelectedObjectID, 
    setZoomPercentage, 
    resetZoomPercentage,
    setSelectedTransformControl,
    selectObjectKeepingTool,
    setIsTargetImageListItemClicked,
    setIsObjectListItemClicked,
    setLastDeletedTargetImageData,
    setIsUpdatingFromEditorWorkspace,
    setShouldUpdateObjectState,
    setTargetImageSelection,
    undo,
    redo,
    loadProjectData,
    markSaved,
    clearHistory,
} = editorSlice.actions;

export default editorSlice.reducer;